import { useState, useCallback, useRef } from "react"
import {
  fetchQualifiedQueue,
  fetchQualificationDetail,
  rescoreLead,
  startQualification,
} from "../api/qualificationApi"

// ── useQualifiedQueue ─────────────────────────────────────────────────────────
// Ranked queue + per-band counts + ROI stats, with band/verdict/search filters.
export function useQualifiedQueue() {
  const [data, setData] = useState(null) // { leads, counts, stats, meta }
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const abortRef = useRef(null)

  const fetch = useCallback(async (filters = {}) => {
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()

    setLoading(true)
    setError(null)
    try {
      setData(await fetchQualifiedQueue(filters))
    } catch (err) {
      if (err.name !== "AbortError") setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  return { data, loading, error, fetch, refresh: fetch }
}

// ── useQualificationDetail ────────────────────────────────────────────────────
export function useQualificationDetail() {
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(async (id) => {
    setLoading(true)
    setDetail(null)
    try {
      setDetail(await fetchQualificationDetail(id))
    } finally {
      setLoading(false)
    }
  }, [])

  const clear = useCallback(() => setDetail(null), [])

  return { detail, loading, load, clear }
}

// ── useQualificationActions ───────────────────────────────────────────────────
export function useQualificationActions() {
  const [pending, setPending] = useState(false)

  const rescore = useCallback(async (id) => {
    setPending(true)
    try { return await rescoreLead(id) } finally { setPending(false) }
  }, [])

  const start = useCallback(async (id) => {
    setPending(true)
    try { return await startQualification(id) } finally { setPending(false) }
  }, [])

  return { rescore, start, pending }
}
